import type { CSSProperties, ReactNode } from 'react'
import { CommandPalette } from './ui-kit/index.js'
import { NotificationBell } from './NotificationBell.jsx'
import type { AuthUser } from './contexts/AuthContext'
import { useOperatorProfile } from './contexts/OperatorProfileContext.jsx'
import { SocialButtons } from './SocialIcons.jsx'

// Permissive style sheet shape — `S` carries both plain CSSProperties
// and callable style helpers (S.navItem(active), S.topStatusDot(tone),
// etc.). Wave 17 (ui-kit migration) replaces these with typed primitives.
type StyleSheet = Record<string, unknown>

function formatShellDate(): string {
  return new Date().toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export interface NavItem {
  id: string
  label: string
  icon?: ReactNode
  group?: string
  matches?: string[]
}

export interface NavGroup {
  id: string
  label: string
}

export interface IntegrationEntry {
  key: string
  label?: string
  status?: string
  available?: boolean
  enabled?: boolean
  [k: string]: unknown
}

export interface CommandResult {
  id: string
  label: string
  description?: string
  [k: string]: unknown
}

export interface AppShellProps {
  S: StyleSheet
  btn: (variant: string, ...rest: unknown[]) => CSSProperties
  nav: NavItem[]
  navGroups?: NavGroup[]
  tab: string
  setTab: (next: string) => void
  navCounts: Record<string, number>
  liTokenWarning?: boolean
  isMobile: boolean
  railCollapsed?: boolean
  railOpen?: boolean
  setRailOpen: (open: boolean) => void
  sidebarStyle?: CSSProperties
  topTitle: ReactNode
  topSub?: ReactNode
  commandOpen: boolean
  setCommandOpen: (open: boolean) => void
  commandQuery: string
  setCommandQuery: (next: string) => void
  commandResults: CommandResult[]
  onCommandSelect: (result: CommandResult) => void
  durableStateNote: string
  readyIntegrations: number
  integrationEntries: IntegrationEntry[]
  healthTone: string
  archiveCount: number
  reportCount: number
  publishAttemptsCount: number
  authEnabled?: boolean
  authUser?: AuthUser | null
  onLogout?: () => void
  children?: ReactNode
}

// Helpers to coerce callable style functions out of the permissive
// StyleSheet without scattering casts everywhere.
function callFn<T>(s: unknown, ...args: unknown[]): T {
  if (typeof s === 'function') return (s as (...a: unknown[]) => T)(...args)
  return s as T
}

export function AppShell({
  S,
  btn,
  nav,
  navGroups,
  tab,
  setTab,
  navCounts,
  liTokenWarning,
  isMobile,
  railCollapsed,
  railOpen,
  setRailOpen,
  sidebarStyle,
  topTitle,
  topSub,
  commandOpen,
  setCommandOpen,
  commandQuery,
  setCommandQuery,
  commandResults,
  onCommandSelect,
  durableStateNote,
  readyIntegrations,
  integrationEntries,
  healthTone,
  archiveCount,
  reportCount,
  publishAttemptsCount,
  authEnabled,
  authUser,
  onLogout,
  children,
}: AppShellProps) {
  const { profile } = useOperatorProfile()
  const initials = (() => {
    const name = profile?.name || 'Ignazio De Santis'
    const parts = name.trim().split(/\s+/)
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  })()
  const systemSummary = [
    { label: 'Durability', value: durableStateNote },
    {
      label: 'Readiness',
      value: `${readyIntegrations}/${integrationEntries.length} ready`,
    },
  ]

  const operatorSummary = [
    {
      label: 'Storage',
      value: `${archiveCount} archived · ${reportCount} reports`,
    },
    { label: 'Publish log', value: `${publishAttemptsCount} attempts` },
  ]

  return (
    <div style={S.app as CSSProperties}>
      {isMobile && railOpen ? (
        <button
          style={S.shellBackdrop as CSSProperties}
          onClick={() => setRailOpen(false)}
          aria-label="Close navigation"
        />
      ) : null}

      {commandOpen ? (
        <CommandPalette
          open={commandOpen}
          query={commandQuery}
          onQueryChange={setCommandQuery}
          results={commandResults}
          onSelect={onCommandSelect}
          onClose={setCommandOpen}
        />
      ) : null}

      <div style={sidebarStyle}>
        <div style={S.brand as CSSProperties}>
          <div style={S.brandName as CSSProperties}>
            {railCollapsed ? 'IS' : 'Ignazio Studio'}
          </div>
          {!railCollapsed ? (
            <div style={S.brandSub as CSSProperties}>Command center · v3.4</div>
          ) : null}
        </div>

        <nav
          style={S.nav as CSSProperties}
          aria-label="Command center workspaces"
        >
          {/* Wave 2.3 — sidebar grouping. The previous flat 13-item list
              treated the daily publishing loop and admin-tier surfaces
              identically. NAV items now carry a `group` field; we
              render a quiet eyebrow before the first item of each
              group so the operator's eye lands on Daily / Workshop /
              Control without any new chrome. The `matches` array also
              lets one nav item (e.g. Posts) own multiple legacy tab
              IDs (drafts/queue/delivered) so the active highlight
              follows sub-tab navigation. */}
          {(() => {
            const renderedGroups = new Set<string>()
            return nav.map((item) => {
              const count = navCounts[item.id] || 0
              const showWarn = item.id === 'settings' && liTokenWarning
              const isActive =
                tab === item.id ||
                (Array.isArray(item.matches) && item.matches.includes(tab))
              const groupId = item.group || ''
              const showGroupLabel =
                !railCollapsed &&
                groupId &&
                !renderedGroups.has(groupId) &&
                navGroups?.some((g) => g.id === groupId)
              if (groupId) renderedGroups.add(groupId)
              const groupLabel = navGroups?.find((g) => g.id === groupId)?.label
              return (
                <div key={item.id}>
                  {showGroupLabel ? (
                    <div className="studio-nav-group-label">{groupLabel}</div>
                  ) : null}
                  <button
                    className="studio-nav-item"
                    style={callFn<CSSProperties>(S.navItem, isActive)}
                    onClick={() => {
                      setTab(item.id)
                      if (isMobile) setRailOpen(false)
                    }}
                    title={item.label}
                    aria-current={isActive ? 'page' : undefined}
                    aria-label={`${item.label}${count > 0 ? `, ${count}` : ''}`}
                  >
                    <span
                      style={callFn<CSSProperties>(
                        S.navItemIndicator,
                        isActive
                      )}
                    />
                    <span style={callFn<CSSProperties>(S.navIcon, isActive)}>
                      {item.icon}
                    </span>
                    {!railCollapsed ? <span>{item.label}</span> : null}
                    {!railCollapsed && count > 0 ? (
                      <span style={S.navBadge as CSSProperties}>{count}</span>
                    ) : null}
                    {showWarn && count === 0 ? (
                      <span style={S.navWarn as CSSProperties}>!</span>
                    ) : null}
                  </button>
                </div>
              )
            })
          })()}
        </nav>

        {!railCollapsed ? (
          <div className="studio-rail-footer">
            <div className="studio-rail-identity">
              <span
                aria-hidden="true"
                className={`studio-rail-identity-avatar ${
                  profile?.avatarDataUrl ? 'has-image' : ''
                }`.trim()}
              >
                {profile?.avatarDataUrl ? (
                  <img src={profile.avatarDataUrl} alt="" />
                ) : (
                  initials
                )}
              </span>
              <div className="studio-rail-identity-text">
                <strong>{profile?.name || 'Ignazio De Santis'}</strong>
                <span>{profile?.role || 'Operator'}</span>
              </div>
            </div>
            <div className="studio-rail-status">
              <span
                style={callFn<CSSProperties>(S.topStatusDot, healthTone)}
                aria-hidden="true"
              />
              <span>{systemSummary[1]!.value}</span>
            </div>
            <div className="studio-rail-meta">
              <span>{operatorSummary[0]!.value}</span>
              <span aria-hidden="true">•</span>
              <span className="studio-rail-status-day">
                {formatShellDate()}
              </span>
            </div>
            <SocialButtons profile={profile} size={14} />
          </div>
        ) : null}
      </div>

      <div style={S.main as CSSProperties}>
        <div style={S.topbar as CSSProperties}>
          <div style={S.topbarLeft as CSSProperties}>
            {isMobile ? (
              <button
                className="studio-btn studio-btn-outline"
                style={btn('outline')}
                onClick={() => setRailOpen(true)}
                aria-label="Open navigation"
              >
                ☰
              </button>
            ) : null}
            <div>
              <div style={S.topTitle as CSSProperties}>{topTitle}</div>
              {!isMobile ? (
                <div style={S.topSub as CSSProperties}>{topSub}</div>
              ) : null}
            </div>
          </div>

          <div style={S.topbarGroup as CSSProperties}>
            {!isMobile ? (
              <>
                <span style={S.shellIdentityTag as CSSProperties}>
                  @IGNAZIODESANTIS
                </span>
                <span style={S.topSub as CSSProperties}>
                  {formatShellDate()}
                </span>
              </>
            ) : null}
            {authEnabled ? (
              <button
                className="studio-btn studio-btn-outline"
                style={btn('outline')}
                onClick={onLogout}
                title={authUser?.label || 'Studio session'}
              >
                Sign out
              </button>
            ) : null}
            <NotificationBell />
            <button
              className="studio-btn studio-btn-outline"
              style={S.shellCommandButton as CSSProperties}
              onClick={() => setCommandOpen(true)}
              aria-label="Open command palette"
              title={`${readyIntegrations} of ${integrationEntries.length} integrations ready`}
            >
              ⌘K
            </button>
          </div>
        </div>

        <div
          className="studio-fade-in"
          style={{
            ...(S.content as CSSProperties),
            padding: isMobile ? 20 : 28,
          }}
          role="main"
          aria-label={typeof topTitle === 'string' ? topTitle : undefined}
        >
          {children}
        </div>
      </div>
    </div>
  )
}
